class Game {
  constructor(numbers) {
    this.numbers = numbers;
  }

  encode() {
    return this.numbers.map(row => row.join(',')).join(';');
  }

  static decode(code) {
    return new Game(code.split(';').map(row => row.split(',').map(Number)));
  }

  hasSubrectangleSum(grid) {
    const rows = grid.length;
    const cols = grid[0].length;

    // Iterate over all pairs of row boundaries
    for (let top = 0; top < rows; top++) {
      const colSums = new Array(cols).fill(0);

      for (let bottom = top; bottom < rows; bottom++) {
        // Update column sums between rows top and bottom
        for (let col = 0; col < cols; col++) {
          colSums[col] += grid[bottom][col];
        }

        // Now check if any subarray in colSums sums to target
        const seen = new Set([0]);
        let acc = 0;
        for (const val of colSums) {
          acc += val;
          if (seen.has(acc - 10)) {
            return true;
          }
          seen.add(acc);
        }
      }
    }

    return false;
  }

  isDone() {
    return !this.hasSubrectangleSum(this.numbers);
  }

  isClear() {
    for (let i = 0; i < this.numbers.length; i++) {
      for (let j = 0; j < this.numbers[i].length; j++) {
        if (this.numbers[i][j] !== 0) {
          return false;
        }
      }
    }
    return true;
  }

  getNumbers() {
    return this.numbers;
  }

  sum() {
    return this.numbers.flat().reduce((a, b) => a + b, 0);
  }

  withNumbers(numbers) {
    return new Game(numbers);
  }


  getValidMoves(numbers) {
    const moves = [];
    const rows = numbers.length;
    const cols = numbers[0].length;

    // Build prefix sum table
    // prefix[r][c] = sum of all elements from (0,0) to (r-1,c-1)
    const prefix = Array(rows + 1).fill(null).map(() => Array(cols + 1).fill(0));
    
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        prefix[r + 1][c + 1] = numbers[r][c] 
          + prefix[r][c + 1] 
          + prefix[r + 1][c] 
          - prefix[r][c];
      }
    }

    // Helper to get sum of rectangle from (r1,c1) to (r2,c2) inclusive
    const getRectSum = (r1, c1, r2, c2) => {
      return prefix[r2 + 1][c2 + 1] 
        - prefix[r1][c2 + 1] 
        - prefix[r2 + 1][c1] 
        + prefix[r1][c1];
    };

    for (let r1 = 0; r1 < rows; r1++) {
      for (let c1 = 0; c1 < cols; c1++) {
        for (let r2 = r1; r2 < rows; r2++) {
          for (let c2 = c1; c2 < cols; c2++) {
            if (getRectSum(r1, c1, r2, c2) === 10) {
              moves.push({ top: r1, left: c1, bottom: r2, right: c2 });
            }
          }
        }
      }
    }
    
    return moves;
  }

  isClearable(maxIterations = undefined) {
    let numbers = structuredClone(this.numbers);
    let iterations = 0
    const rows = numbers.length;
    const cols = numbers[0].length;

    // Pre-compute initial total
    let initialTotal = 0;
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        initialTotal += numbers[r][c];
      }
    }

    if (initialTotal === 0) {
      return true;
    }

    // Store total alongside state to avoid recomputing
    const stack = [{ grid: numbers, total: initialTotal }];
    const visited = new Set();

    while (stack.length > 0) {
      iterations++;
      if (maxIterations !== undefined && iterations > maxIterations) {
        console.warn("Max iterations reached in isClearable(); aborting");
        return null;
      }
      const { grid, total } = stack.pop();

      // Faster key generation using typed approach
      const key = grid.map(row => row.join('')).join('|');
      if (visited.has(key)) {
        continue;
      }
      visited.add(key);

      const moves = this.getValidMoves(grid);
      if (moves.length === 0) {
        continue;
      }

      // Compute move metadata once, not during sort comparisons
      const movesWithMeta = moves.map(move => {
        let maxVal = 0;
        let moveSum = 0;
        for (let r = move.top; r <= move.bottom; r++) {
          for (let c = move.left; c <= move.right; c++) {
            const val = grid[r][c];
            if (val > maxVal) maxVal = val;
            moveSum += val;
          }
        }
        return { move, maxVal, moveSum };
      });

      // Sort by max value descending
      movesWithMeta.sort((a, b) => a.maxVal - b.maxVal);

      for (let i = 0; i < movesWithMeta.length; i++) {
        const { move, moveSum } = movesWithMeta[i];
        const newTotal = total - moveSum;

        // Early win check before creating new state
        if (newTotal === 0) {
          return true;
        }

        const newGrid = grid.map(row => row.slice());
        for (let r = move.top; r <= move.bottom; r++) {
          for (let c = move.left; c <= move.right; c++) {
            newGrid[r][c] = 0;
          }
        }
        stack.push({ grid: newGrid, total: newTotal });
      }
  }

  return false;
}

}

export default Game;
